// packages/web-ui/src/components/inline-cards/ResponseViewProposalCard.tsx
//
// Inline response-view card rendered in chat after `kb:viewRendered`.
// The server's NodeAutoViewWriter writes the view directly to its permanent
// path before emitting the event, so there is no "proposal" / "discarded"
// state to manage — every view is already saved. The card shows the
// React-Flow preview with two affordances:
//   - click the preview → expand into a lightbox modal (quick visual)
//   - "Edit in Views" button → open the view in the Views tab editor
//
// TRUST: SVG / layout comes from the server-trusted `syside viz` binary;
// not user content. See
// packages/webview-ui/src/panels/chat-panel/components/response-view-proposal-card.ts:26-28
// for the Lit precedent.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX, KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { ReactFlowLayoutJson } from '@tessa-lean/base/api-schemas/index.js';
import { resolveHiddenNodeIds, defaultViewScope } from '@tessa-lean/base';
import { CardShell } from './CardShell.js';
import { MiniReactFlowPreview } from './MiniReactFlowPreview.js';
import { ViewImageModal, ILLUSTRATIVE_BADGE_COPY } from './ViewImageModal.js';
import { useLlmImageEnabled } from '../../hooks/useLlmImageEnabled.js';
import { type ResponseViewProposalEnvelopeData } from '../../state/inline-cards.js';

export interface ResponseViewProposalCardProps
  extends ResponseViewProposalEnvelopeData {
  /**
   * Fetches the ReactFlow layout JSON for the view. Returns the layout OR
   * null on error. Called once on mount.
   */
  onFetchLayout: (viewId: string) => Promise<ReactFlowLayoutJson | null>;
  /** Open the rendered view in the Views tab. */
  onOpen: (viewId: string) => void;
  /**
   * Optional explicit-save handler. When provided, the card surfaces a
   * "Save" button that re-PUTs the current layout (idempotent — the view
   * is already on disk) and flashes a confirmation toast. When omitted
   * the Save affordance is hidden (back-compat for tests / older callers).
   */
  onSave?: (
    viewId: string,
    layout: ReactFlowLayoutJson,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /** Honour `prefers-reduced-motion: reduce`. Default: auto-detect. */
  reducedMotion?: boolean;
  /**
   * IV3-S2 / MAJ-11 — fetch the raw `view.sysml` for the SysML-source tab.
   * Returns the source text or null on error. When omitted the segmented
   * control still renders (pillar invariant) but the SysML tab shows an
   * "unavailable" state. Production wiring (ChatPanel) resolves the view
   * manifest's `sysmlSourcePath` and reads it via the files route
   * (`GET /api/v1/files/content?path=…`) — the dead `GET
   * /api/v1/views/<id>/sysml` 501 stub + its `viewsApi.getSysmlSource` client
   * method were removed (AR-5).
   */
  onFetchSysmlSource?: (viewId: string) => Promise<string | null>;
  /**
   * LV3-S1 — opt-in "Generate LLM visualization" follow-up. When provided AND
   * the view is `rasterOffered` AND `llmImageEnabled` (and no raster exists
   * yet), the card surfaces a CONVERSATIONAL offer block: a short line telling
   * the user an AI render is available as a next step — it can honour extra
   * layout wishes (an optional free-text input) but takes ~a minute — plus the
   * Generate action. The handler mounts the async illustrative-image skeleton
   * card via the image-views slice and POSTs `/views/:viewId/image`. Returns a
   * 202-accept Result — the produced raster lands later as a SIBLING async card
   * (`image:genReady`); this card keeps the deterministic sketch in place. On a
   * non-OK accept the card surfaces an inline notice (no infinite spinner).
   *
   * `layoutWishes` (the offer's optional input) is forwarded as the raster's
   * scene-content instruction; the server layers it ADDITIVELY over the KB
   * scene so the diagram stays grounded. When the user typed no wishes the card
   * calls the handler with a SINGLE arg (no `layoutWishes`) so a plain generate
   * grounds purely on the KB scene. Omitted ⇒ no offer block (switch-OFF /
   * back-compat for tests + older callers).
   */
  onGenerateImage?: (
    viewId: string,
    layoutWishes?: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;

  /**
   * LV3-S2 — the sticky per-view LEAD engine (LV2-S2). Chooses which
   * representation the Diagram tab leads with: `'sketch'` (the deterministic
   * React Flow preview, the always-on default) or `'llm-image'` (the generated
   * raster). Absent ⇒ `'sketch'` (mirrors `effectiveLeadEngine`). The other
   * representation + the SysML source stay one action away regardless — the
   * lead only chooses the default surface, it never removes the alternative.
   */
  leadEngine?: 'sketch' | 'llm-image';
  /**
   * LV3-S2 — true when a generated raster EXISTS for this view (ChatPanel
   * derives this from the image-views slice: a record whose `status === 'ready'`
   * for the matching `viewId`). The Sketch ⇄ LLM-image switch renders ONLY when
   * BOTH representations exist (`rasterReady === true && onSetLeadEngine` wired).
   * Absent / false ⇒ no switch — the card is byte-identical to the sketch-only /
   * switch-OFF path (the deterministic sketch leads alone).
   */
  rasterReady?: boolean;
  /** LV3-S2 — PNG URL of the generated raster (image-views `imageUrl`). */
  rasterImageUrl?: string;
  /** LV3-S2 — alt text for the raster lead surface (a11y). */
  rasterAlt?: string;
  /**
   * LV3-S2 — persist the sticky per-view lead engine (the `views` slice
   * `setLeadEngine`, which optimistically patches `manifest.leadEngine` then
   * PUTs `/views/:viewId/lead-engine`). Returns a Result; on a non-OK result the
   * card rolls the local engine choice back and surfaces an inline notice. When
   * omitted the engine switch is hidden (back-compat / switch-OFF).
   */
  onSetLeadEngine?: (
    viewId: string,
    leadEngine: 'sketch' | 'llm-image',
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  /**
   * LV3-S3 — the inline "adjust layout" refinement loop. When provided AND the
   * Diagram tab is leading with the deterministic sketch (`effectiveEngine ===
   * 'sketch'`), the card surfaces a small prompt to tweak the CURRENT view's
   * arrangement in natural language ("move the BMS up", "spread it out").
   *
   * Production wiring (ChatPanel) is a one-line `(viewId, request) =>
   * sendMessage(sessionId, request)` wrapper: the request flows through the
   * SAME chat send path as a typed message, so the server's LV1-S6 routing
   * (`chat-message-handler` → `classifyLayoutRequest` → `relayoutView`) owns
   * the whole loop. On `applied: true` the server persists the new intent
   * (LV1-S5) and re-emits `kb:viewRendered` for the SAME viewId — this card
   * re-renders IN PLACE (~1–2 s client ELK) via the existing views/image-views
   * state already feeding this `viewId`-keyed card; the card adds NO local
   * `kb:viewRendered` listener (that would double-render). On compiler
   * `unsupported: true` the server returns `escalation: 'expressiveness-gap'`
   * and the LV2-S3 policy fires the escalated raster as a SIBLING card — the
   * handoff is server-owned, so this card NEVER re-shows the unchanged sketch
   * for an unsupported request. The Result it returns is only the 202-accept of
   * the send itself (a non-OK accept surfaces an inline notice; no spinner is
   * left hanging).
   *
   * This loop is the always-on deterministic refinement path: it is gated ONLY
   * on the handler being wired + the sketch leading — NOT on
   * `views.llmImage.enabled` (the sketch tweak must work even when raster is
   * OFF). Omitted ⇒ no prompt (back-compat for tests + older callers; the
   * switch-OFF / raster-disabled card stays byte-identical).
   */
  onAdjustLayout?: (
    viewId: string,
    request: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;

  /**
   * The FOCUSED expand-modal's edit channel. When provided, the modal composer
   * routes through THIS handler instead of `onAdjustLayout` — the production
   * wiring posts to the view-scoped diagram agent (`POST /views/:viewId/edit`),
   * so a focused-view edit executes against this view and re-renders in place
   * WITHOUT landing a turn in the normal chat. The inline card prompt is
   * unaffected — it keeps using `onAdjustLayout` (the chat path). Omitted ⇒ the
   * modal composer falls back to `onAdjustLayout` (back-compat for tests /
   * older callers).
   */
  onEditViewContent?: (
    viewId: string,
    request: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;

  /**
   * Phase 2 — clear this view's scope ("Show all" reveal). When provided AND the
   * view is currently scoped (some parts hidden/collapsed), the card shows a
   * non-dismissible "N parts hidden" badge with a "Show all" button that calls
   * this handler (ChatPanel → `views` slice `clearScope` → `PUT
   * /views/:viewId/scope` with an empty scope, which the server strips). On `ok`
   * the card re-fetches the layout so the full node set renders. Omitted ⇒ the
   * badge renders without a button (still a faithfulness signal), or the whole
   * badge is hidden when the view is unscoped. The raw `view.sysml` is never
   * touched — a scoped sketch is a faithful partial; the full model stays one
   * action away under "SysML source".
   */
  onClearScope?: (
    viewId: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;

  /**
   * LV3-S4 — the LV2-S3 escalation reason recorded for this view when it was
   * AUTO-escalated to the `llm-image` lead (an expressiveness gap the precise
   * renderer couldn't honour, or detected frustration). Mirrors the server's
   * `EscalationReason` string union (`'expressiveness-gap' | 'frustration'`) —
   * kept as a local literal (not an import) since the producing type lives in
   * `@tessa-lean/server`, which web-ui must not import. Absent ⇒ the view was
   * NOT auto-escalated (manual "Generate LLM visualization" or a plain sketch),
   * so NO escalation rationale renders — the card stays byte-identical to the
   * non-escalated path. The calm rationale + the prominent "Back to sketch"
   * control render only when this is set AND a raster is genuinely
   * available/loading (see `rasterLoading`) — the LV2-S5 follow-up: never
   * explain an escalation whose image will never appear (e.g. a silent
   * fallback-on-error already fired).
   */
  escalated?: 'expressiveness-gap' | 'frustration';
  /**
   * LV3-S4 — true while the escalated raster's async image-views record is
   * `generating` (ChatPanel derives this from the image-views slice:
   * `status === 'generating'` for the matching `viewId`). Combined with
   * `rasterReady` (the `status === 'ready'` signal) this is the LV2-S5 guard:
   * the escalation rationale renders when the image is ready OR in flight, and
   * NEVER when no image will appear. Absent / false ⇒ not loading; if
   * `rasterReady` is also falsy the rationale stays hidden even when
   * `escalated` is set. (If ChatPanel cannot yet supply this, the guard
   * degrades to `rasterReady`-only — still correct: the notice then shows only
   * once the image exists. Wiring `rasterLoading` is the LV3-S5 ChatPanel
   * follow-up.)
   */
  rasterLoading?: boolean;
  /**
   * True when the image-views slice reports this view's raster errored /
   * filtered. Forwarded ONLY to the focused expand modal so a modal-initiated
   * generate that fails asynchronously resolves its in-place spinner at once
   * (see `ViewImageModal`'s `rasterError`). Unused by the inline card body.
   */
  rasterError?: boolean;
}

function useDetectReducedMotion(override: boolean | undefined): boolean {
  const [auto, setAuto] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (): void => setAuto(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return override ?? auto;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type SourceTab = 'diagram' | 'sysml';
// LV3-S1 — local state for the opt-in "Generate LLM visualization" follow-up.
// `requested` is the terminal happy path: the 202 accept landed and the raster
// now lives in its OWN sibling async card (image-views slice) — this card never
// owns the raster lifecycle, so there is no spinner to leave hanging.
type GenerateStatus = 'idle' | 'generating' | 'requested' | 'error';
// LV3-S2 — engine switcher (Sketch ⇄ LLM image). `LeadEngine` is the per-view
// sticky lead representation; `SetLeadStatus` tracks the persist round-trip so a
// failed PUT can roll the local choice back + surface a notice.
type LeadEngine = 'sketch' | 'llm-image';
type SetLeadStatus = 'idle' | 'saving' | 'error';
// LV3-S3 — inline "adjust layout" refinement loop. `submitting` covers the
// 202-accept round-trip of the chat send; `submitted` is the terminal happy
// path — the re-laid-out sketch (or, on `unsupported`, the escalated raster
// sibling) arrives via the existing view/image-views state, NOT owned here, so
// there is no spinner to leave hanging.
type AdjustStatus = 'idle' | 'submitting' | 'submitted' | 'error';
// Phase 2 — "Show all" scope-clear round-trip. `clearing` covers the PUT; on ok
// the card re-fetches the (now-unscoped) layout so the badge auto-hides.
type ScopeStatus = 'idle' | 'clearing' | 'error';

/**
 * Humanize an opaque transport error into calm, user-facing copy. A raw
 * proxy/gateway failure reaches the card as a bare `"500 Internal Server
 * Error"` (or `"502 Bad Gateway"`, or an empty string) — the SPA api-call
 * fallback when the response body is NOT our JSON error envelope (e.g. the dev
 * proxy's own error page emitted when the backend connection resets mid-flight).
 * Surfacing that raw status to the user is alarming and unactionable, so any
 * bare-HTTP-status / fetch-failure message is translated to a calm "temporarily
 * unavailable, please try again". A real, specific message (rate limit,
 * validation, disk full, "layout service unavailable") is passed through
 * verbatim so we never hide an actionable detail. Matching is anchored to
 * WHOLE-string bare status phrases so an embedded word never trips it.
 */
export function humanizeServiceError(message: string): string {
  const m = message.trim();
  if (
    m.length === 0 ||
    // Bare status. `(\s|$)` so a code with an EMPTY statusText — the norm on
    // HTTP/2, where browsers null the reason phrase, leaving `"500 "` → trimmed
    // to `"500"` — is still caught (the raw-status leak this fix exists to stop).
    // `\d{3}(\s|$)` still won't swallow a real "500ms timeout" (no trailing ws).
    /^\d{3}(\s|$)/.test(m) ||
    /^(internal server error|bad gateway|gateway time-?out|service unavailable)$/i.test(m) ||
    // Prefix (no `$`) so Firefox's "NetworkError when attempting to fetch
    // resource" matches alongside Chrome "Failed to fetch" / Safari "Load failed".
    /^(failed to fetch|fetch failed|network ?error|load failed)/i.test(m)
  ) {
    return 'the service was temporarily unavailable — please try again in a moment';
  }
  return m;
}

export function ResponseViewProposalCard(
  props: ResponseViewProposalCardProps,
): JSX.Element {
  const [layout, setLayout] = useState<ReactFlowLayoutJson | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(true);
  const [previewError, setPreviewError] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  // LV3-S2 — separate lightbox for the raster lead surface (image-mode modal).
  const [rasterModalOpen, setRasterModalOpen] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // LV3-S1 — opt-in raster follow-up local state. Distinct from saveStatus so
  // the two affordances never collide on the shared toast.
  const [generateStatus, setGenerateStatus] = useState<GenerateStatus>('idle');
  const [generateError, setGenerateError] = useState<string | null>(null);
  // LV3-S1 (enhanced) — the offer's optional "layout wishes" free-text. Forwarded
  // to `onGenerateImage(viewId, wishes)` as the raster's additive scene-content
  // instruction. Empty ⇒ the handler is called single-arg (plain KB-grounded
  // generate).
  const [layoutWishesText, setLayoutWishesText] = useState<string>('');
  // Opt-in disclosure gate for the raster follow-up. The "Generate LLM
  // visualization" action is paid, ~a minute long, and illustrative-only, so it
  // is collapsed behind a drop-down the user must ACTIVATE (open) before the
  // layout-wishes input + Generate button appear. Default collapsed keeps the
  // deterministic sketch the uncluttered default surface and prevents accidental
  // triggering of the image path.
  const [offerExpanded, setOfferExpanded] = useState<boolean>(false);

  // LV3-S2 — engine-switcher local state. `engine` mirrors the sticky lead
  // (seeded from props, defaulting to 'sketch'); `setLeadStatus`/`setLeadError`
  // track the PUT round-trip so a failed persist rolls the local choice back and
  // surfaces an inline notice. The two-element-tablist roving focus reuses the
  // source-tabs pattern via dedicated refs.
  const [engine, setEngine] = useState<LeadEngine>(props.leadEngine ?? 'sketch');
  const [setLeadStatus, setSetLeadStatus] = useState<SetLeadStatus>('idle');
  const [setLeadError, setSetLeadError] = useState<string | null>(null);
  // LV3-S4 — durable local un-escalation. Set once a "Back to sketch" PUT
  // succeeds so the escalation rationale does NOT reappear on the next
  // re-render even though the (broadcast-once) `escalated` prop persists. The
  // server-side reset (leadEngine → 'sketch') is the durable cross-reload half;
  // this flag is the within-session-render half. Reset if a FRESH escalation
  // reason arrives for this card (a later turn re-escalated the same view).
  const [escalationDismissed, setEscalationDismissed] = useState<boolean>(false);
  const lastEscalationReasonRef = useRef<typeof props.escalated>(props.escalated);
  const engineSketchTabRef = useRef<HTMLButtonElement>(null);
  const engineImageTabRef = useRef<HTMLButtonElement>(null);

  // LV3-S3 — inline "adjust layout" refinement loop local state. `adjustText`
  // is the controlled prompt value; `adjustStatus`/`adjustError` track the
  // 202-accept round-trip. Distinct from the save/generate statuses so the
  // three affordances never collide on a shared notice.
  const [adjustText, setAdjustText] = useState<string>('');
  const [adjustStatus, setAdjustStatus] = useState<AdjustStatus>('idle');
  const [adjustError, setAdjustError] = useState<string | null>(null);

  // Phase 2 — "Show all" scope-clear local state.
  const [scopeStatus, setScopeStatus] = useState<ScopeStatus>('idle');
  const [scopeError, setScopeError] = useState<string | null>(null);

  // IV3-S2 — segmented [ Diagram | SysML source ] control. The SysML source is
  // the pillar invariant: reachable in one action for EVERY view.
  const [tab, setTab] = useState<SourceTab>('diagram');
  const [sysmlSource, setSysmlSource] = useState<string | null>(null);
  const [sysmlLoading, setSysmlLoading] = useState<boolean>(false);
  const [sysmlFetched, setSysmlFetched] = useState<boolean>(false);
  const diagramTabRef = useRef<HTMLButtonElement>(null);
  const sysmlTabRef = useRef<HTMLButtonElement>(null);

  const reducedMotion = useDetectReducedMotion(props.reducedMotion);

  // The focused expand-modal's "Generate AI image" affordance reads the raster
  // master switch LIVE (like the Views tab's `<ViewActionBar>`), NOT the
  // envelope's `llmImageEnabled`. The envelope value is entangled with the
  // server's `rasterOffered` eligibility flag (SPA `attach-to-chat-slice` forces
  // it false whenever `rasterOffered` is false), so a view the server didn't
  // mark offer-eligible would never surface the button even with the switch ON.
  // Reading `views.llmImage.enabled` directly makes the modal button appear
  // whenever raster is enabled for the workspace — matching the Views tab.
  const llmImageEnabledLive = useLlmImageEnabled();

  // One-shot auto-fetch guard, re-keyed on `layoutRevision`. A normal new view
  // mounts with `layoutRevision` absent (≡ 0) and fetches exactly once. A chat
  // "adjust layout" request re-runs the server layout and the SSE handler bumps
  // `layoutRevision` on this card's frozen envelope (attachCard is a replay-safe
  // no-op, so the bump is the only signal a relayout happened); the guard then
  // sees a NEW revision and re-fetches the latest `layout.json` so the inline
  // MiniReactFlowPreview re-renders the new arrangement IN PLACE.
  const autoFetchedRevisionRef = useRef<number | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // R3 — fallback timer that clears a stuck "Adjusting layout…" notice when no
  // in-place re-render arrives (e.g. a no-op change re-emits nothing).
  const adjustTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchLayout = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(false);
    const result = await props.onFetchLayout(props.viewId);
    if (result === null) {
      setPreviewError(true);
      setPreviewLoading(false);
      return;
    }
    setLayout(result);
    setPreviewLoading(false);
  }, [props]);

  const layoutRevision = props.layoutRevision ?? 0;
  useEffect(() => {
    const prev = autoFetchedRevisionRef.current;
    if (prev !== layoutRevision) {
      autoFetchedRevisionRef.current = layoutRevision;
      void fetchLayout();
      // R3 — a relayout re-emit bumped the revision (skip the initial mount,
      // prev === null): the in-place re-render has arrived, so clear the
      // transient "Adjusting layout…" notice instead of leaving it stuck.
      if (prev !== null) {
        if (adjustTimerRef.current !== null) clearTimeout(adjustTimerRef.current);
        setAdjustStatus((s) => (s === 'submitted' ? 'idle' : s));
      }
    }
  }, [fetchLayout, layoutRevision]);

  // Clear any pending toast timer on unmount.
  useEffect(() => {
    return () => {
      if (toastTimerRef.current !== null) {
        clearTimeout(toastTimerRef.current);
      }
      if (adjustTimerRef.current !== null) {
        clearTimeout(adjustTimerRef.current);
      }
    };
  }, []);

  // LV3-S4 — re-arm the escalation rationale when a NEW reason arrives for this
  // card after a prior "Back to sketch" dismissal (a later turn re-escalated the
  // same view). A transition from no-reason → reason, or a changed reason, clears
  // the local dismissal so the fresh escalation is explained again.
  useEffect(() => {
    const prev = lastEscalationReasonRef.current;
    const next = props.escalated;
    if (next !== undefined && next !== prev) {
      setEscalationDismissed(false);
    }
    lastEscalationReasonRef.current = next;
  }, [props.escalated]);

  const handleExpand = useCallback(() => {
    if (layout === null) return;
    setModalOpen(true);
  }, [layout]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    props.onOpen(props.viewId);
  }, [props]);

  const activateSysmlTab = useCallback(async () => {
    setTab('sysml');
    if (sysmlFetched) return;
    const fetcher = props.onFetchSysmlSource;
    if (fetcher === undefined) {
      setSysmlFetched(true);
      return;
    }
    setSysmlLoading(true);
    const text = await fetcher(props.viewId);
    setSysmlSource(text);
    setSysmlLoading(false);
    setSysmlFetched(true);
  }, [props, sysmlFetched]);

  // Roving-tab keyboard nav (Arrow/Home/End move + Enter/Space activate).
  const handleTablistKey = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowRight' || e.key === 'End') {
        e.preventDefault();
        sysmlTabRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'Home') {
        e.preventDefault();
        diagramTabRef.current?.focus();
        return;
      }
    },
    [],
  );

  const handleSave = useCallback(async () => {
    const onSave = props.onSave;
    if (onSave === undefined || layout === null) return;
    setSaveStatus('saving');
    setSaveError(null);
    const result = await onSave(props.viewId, layout);
    if (toastTimerRef.current !== null) {
      clearTimeout(toastTimerRef.current);
    }
    if (result.ok) {
      setSaveStatus('saved');
      toastTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, 2400);
    } else {
      setSaveStatus('error');
      setSaveError(result.message);
      toastTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
        setSaveError(null);
      }, 4000);
    }
  }, [props, layout]);

  // LV3-S1 — fire the opt-in raster generation. The injected handler mounts the
  // async illustrative-image skeleton card (image-views slice) and POSTs
  // `/views/:viewId/image`; the produced raster resolves on `image:genReady` in
  // that SIBLING card with the non-dismissible illustrative badge. This card
  // NEVER swaps its sketch for the raster, so "sketch remains on failure" holds
  // trivially. The local flag guards re-clicks while a 202 is in flight.
  const canOfferRaster =
    props.rasterOffered === true &&
    props.llmImageEnabled === true &&
    props.onGenerateImage !== undefined;

  const handleGenerateImage = useCallback(async () => {
    const onGenerateImage = props.onGenerateImage;
    if (onGenerateImage === undefined) return;
    if (generateStatus === 'generating') return;
    setGenerateStatus('generating');
    setGenerateError(null);
    // Forward the user's optional layout wishes as the raster's additive
    // instruction. When empty, call single-arg so a plain generate grounds
    // purely on the KB scene (and back-compat with callers/tests that expect a
    // one-arg call).
    const wishes = layoutWishesText.trim();
    const result =
      wishes.length > 0
        ? await onGenerateImage(props.viewId, wishes)
        : await onGenerateImage(props.viewId);
    if (result.ok) {
      // 202 accepted — the async sibling card now owns the raster lifecycle
      // (skeleton → genReady/genFailed). Resolve the button to a quiet
      // "requested" state; no spinner is left running here.
      setGenerateStatus('requested');
    } else {
      setGenerateStatus('error');
      setGenerateError(result.message);
    }
  }, [props, generateStatus, layoutWishesText]);

  // LV3-S1 (enhanced) — Enter-to-submit on the layout-wishes input (Shift+Enter
  // left free for any future multi-line affordance; the bare Enter generates).
  const handleWishesKey = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleGenerateImage();
      }
    },
    [handleGenerateImage],
  );

  // LV3-S2 — the engine switch renders ONLY when BOTH representations exist: a
  // generated raster (`rasterReady`) AND a persist handler. Until then the card
  // is byte-identical to the sketch-only path (no switch, sketch leads alone).
  const canSwitchEngine =
    props.rasterReady === true && props.onSetLeadEngine !== undefined;

  // The effective lead surface for the Diagram tab. When the switch is not
  // available the sketch always leads (the switch-OFF invariant); otherwise the
  // local `engine` choice wins. If 'llm-image' is chosen but no raster URL was
  // passed, fall back to the sketch so the Diagram tab is never blank.
  const effectiveEngine: LeadEngine =
    canSwitchEngine && engine === 'llm-image' && props.rasterImageUrl !== undefined
      ? 'llm-image'
      : 'sketch';

  // LV3-S3 — the inline "adjust layout" prompt renders ONLY when a handler is
  // wired AND the Diagram tab is leading with the deterministic sketch (the
  // tweak loop targets the sketch's arrangement; the raster lead has no
  // deterministic layout to nudge). Crucially this is NOT gated on
  // `llmImageEnabled` — the sketch refinement loop is the always-on
  // deterministic path and must work with raster fully OFF. Omitted handler ⇒
  // no prompt (switch-OFF byte-identical).
  const canAdjustLayout =
    props.onAdjustLayout !== undefined && effectiveEngine === 'sketch';

  // Phase 2 — how many parts the current scope hides (0 when unscoped). Derived
  // from the fetched layout's `viewScope`; `resolveHiddenNodeIds` returns the
  // full transitive hidden-id set, which is exactly the count of parts dropped
  // from the sketch. A scoped view shows a non-dismissible faithfulness badge.
  const hiddenCount =
    layout !== null
      ? resolveHiddenNodeIds(layout, layout.viewScope ?? defaultViewScope()).size
      : 0;
  const isScoped = hiddenCount > 0;

  // LV3-S4 — show the calm escalation rationale + "Back to sketch" control ONLY
  // when the view was AUTO-escalated (props.escalated set) AND a raster is
  // genuinely available (`rasterReady`) OR in flight (`rasterLoading`). This is
  // the LV2-S5 follow-up guard: never explain an AI illustration that will
  // never appear (e.g. a silent fallback-on-error already returned the sketch).
  // When `escalated` is omitted the entire subtree is absent — byte-identical
  // to the non-escalated / switch-OFF path.
  const showEscalationNotice =
    props.escalated !== undefined &&
    !escalationDismissed &&
    (props.rasterReady === true || props.rasterLoading === true);

  // LV3-S2 — flip the sticky lead engine. Optimistically sets local state and,
  // when the chosen engine differs from the current local lead, persists via the
  // injected handler. On a non-OK result the local choice rolls back + an inline
  // notice surfaces. Switching to a non-sketch lead forces the visible tab back
  // to 'diagram' so the chosen surface is actually shown (the SysML tab is never
  // gated by the engine — it stays reachable from either lead).
  const handleSelectEngine = useCallback(
    async (next: LeadEngine) => {
      const onSetLeadEngine = props.onSetLeadEngine;
      if (onSetLeadEngine === undefined) return;
      if (setLeadStatus === 'saving') return;
      const prev = engine;
      if (next === prev) {
        // Same engine re-selected — just surface its diagram surface; no PUT.
        setTab('diagram');
        return;
      }
      setEngine(next);
      setTab('diagram');
      setSetLeadStatus('saving');
      setSetLeadError(null);
      const result = await onSetLeadEngine(props.viewId, next);
      if (result.ok) {
        setSetLeadStatus('idle');
      } else {
        // Roll the local lead back so neither representation is lost.
        setEngine(prev);
        setSetLeadStatus('error');
        setSetLeadError(result.message);
      }
    },
    [props, engine, setLeadStatus],
  );

  // LV3-S4 — "Back to sketch" from the escalation rationale. DURABLY un-escalates:
  //  (1) resets the sticky per-view leadEngine to 'sketch' SERVER-SIDE via the
  //      same `onSetLeadEngine` PUT (`PUT /views/:viewId/lead-engine`), so a
  //      reload no longer leads with the raster; AND
  //  (2) clears the LOCAL escalation rationale (`escalationDismissed`) ONLY when
  //      that PUT succeeds, so the calm notice does not reappear on the next
  //      re-render even though the broadcast-once `escalated` prop persists.
  // On a failed PUT we roll the local lead back AND leave the rationale up (the
  // engine-switch notice surfaces the error) so the user can retry — we never
  // silently hide the escalation on an un-persisted reset. When the lead is
  // ALREADY 'sketch' (escalation flagged but a prior switch landed) the PUT is
  // skipped and we dismiss directly — that is the durable target already.
  const handleBackToSketch = useCallback(async () => {
    const onSetLeadEngine = props.onSetLeadEngine;
    if (onSetLeadEngine === undefined) return;
    if (setLeadStatus === 'saving') return;
    // ALWAYS issue the PUT (it is idempotent server-side): when a view escalated,
    // the server set the sticky leadEngine to 'llm-image', so even if THIS card's
    // local `engine` seed is a stale 'sketch' (the views-list manifest hadn't
    // refreshed yet) we must still durably reset the server to 'sketch'. Roll the
    // local lead back to the raster on a failed PUT so neither representation is
    // lost, and keep the rationale visible so the user can retry.
    const prev = engine;
    setEngine('sketch');
    setTab('diagram');
    setSetLeadStatus('saving');
    setSetLeadError(null);
    const result = await onSetLeadEngine(props.viewId, 'sketch');
    if (result.ok) {
      setSetLeadStatus('idle');
      setEscalationDismissed(true);
    } else {
      setEngine(prev);
      setSetLeadStatus('error');
      setSetLeadError(result.message);
    }
  }, [props, engine, setLeadStatus]);

  // LV3-S3 — submit an inline layout tweak. The request flows through the
  // injected `onAdjustLayout` wrapper (production: `sendMessage`), so the
  // server's LV1-S6 routing owns relayout-vs-escalate. On an OK accept the
  // re-laid-out sketch (or the escalated raster sibling, on `unsupported`)
  // arrives via the existing view/image-views state — NOT owned here — so we
  // just clear the input + flag `submitted`. Empty / whitespace-only input is a
  // no-op; a second submit while one is in flight is guarded.
  const handleAdjustLayout = useCallback(async () => {
    const onAdjustLayout = props.onAdjustLayout;
    if (onAdjustLayout === undefined) return;
    if (adjustStatus === 'submitting') return;
    const request = adjustText.trim();
    if (request.length === 0) return;
    setAdjustStatus('submitting');
    setAdjustError(null);
    const result = await onAdjustLayout(props.viewId, request);
    if (result.ok) {
      // 202 accepted — the relayout / scope / escalation lands via the re-emitted
      // kb:viewRendered (in-place) or the sibling raster card. Clear the prompt
      // so the next tweak starts fresh. The "Adjusting layout…" notice is cleared
      // the moment the in-place re-render arrives (the layoutRevision bump above);
      // this fallback timer clears it even when nothing re-emits (a no-op change),
      // so the notice is never left permanently stuck.
      setAdjustStatus('submitted');
      setAdjustText('');
      if (adjustTimerRef.current !== null) clearTimeout(adjustTimerRef.current);
      adjustTimerRef.current = setTimeout(() => {
        setAdjustStatus((s) => (s === 'submitted' ? 'idle' : s));
      }, 4000);
    } else {
      setAdjustStatus('error');
      setAdjustError(result.message);
    }
  }, [props, adjustText, adjustStatus]);

  // Phase 2 — "Show all": clear this view's scope so every part renders again.
  // Flows through the injected `onClearScope` wrapper (production: ChatPanel →
  // `views` slice `clearScope` → PUT /views/:viewId/scope). On an OK clear we
  // re-fetch the (now-unscoped) layout so the preview re-renders full and the
  // badge auto-hides (its count recomputes to 0). Guarded against a double-click.
  const handleShowAll = useCallback(async () => {
    const onClearScope = props.onClearScope;
    if (onClearScope === undefined) return;
    if (scopeStatus === 'clearing') return;
    setScopeStatus('clearing');
    setScopeError(null);
    const result = await onClearScope(props.viewId);
    if (result.ok) {
      setScopeStatus('idle');
      await fetchLayout();
    } else {
      setScopeStatus('error');
      setScopeError(result.message);
    }
  }, [props, scopeStatus, fetchLayout]);

  // Enter-to-submit on the adjust-layout input (Shift+Enter is left free for
  // any future multi-line affordance; the bare Enter submits).
  const handleAdjustKey = useCallback(
    (e: ReactKeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleAdjustLayout();
      }
    },
    [handleAdjustLayout],
  );

  // Roving-tab keyboard nav for the engine switch (two tabs).
  const handleEngineTablistKey = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowRight' || e.key === 'End') {
        e.preventDefault();
        engineImageTabRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'Home') {
        e.preventDefault();
        engineSketchTabRef.current?.focus();
        return;
      }
    },
    [],
  );

  const transitionClass = reducedMotion ? 'transition-none' : 'transition-opacity duration-100';

  return (
    <CardShell
      role="region"
      ariaLabel={`View: ${props.viewName}`}
      cardKind="response-view-proposal"
      testId="response-view-proposal-card"
      className={transitionClass}
    >
      <div className="flex flex-col gap-2">
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-medium text-fg" data-testid="response-view-proposal-title">
              {layout?.title ?? props.viewName}
            </div>
            <span
              data-testid="response-view-proposal-saved-indicator"
              aria-label="View saved to disk"
              className="text-[10px] font-medium uppercase tracking-wide text-emerald-700"
            >
              ✓ Saved
            </span>
          </div>
          {typeof props.elementCount === 'number' && props.elementCount > 0 ? (
            <div className="text-xs text-taupe-700">
              {props.elementCount} elements
            </div>
          ) : null}
          {typeof props.sourceQuestion === 'string' && props.sourceQuestion.length > 0 ? (
            <div className="text-xs text-taupe-700 italic">
              from: {props.sourceQuestion}
            </div>
          ) : null}
        </div>

        {/* LV3-S2 — Sketch ⇄ LLM-image engine switch. Renders ONLY when a
            generated raster exists for this view AND a persist handler is wired
            (both representations present). It chooses which engine the Diagram
            tab leads with + reflects/updates the sticky per-view `leadEngine`
            (LV2-S2). The [Diagram | SysML source] control below is untouched, so
            "View SysML source" stays reachable in one action under EITHER engine
            (the pillar invariant). Absent ⇒ byte-identical sketch-only path. */}
        {canSwitchEngine ? (
          <div
            role="tablist"
            aria-label="Lead representation"
            data-testid="response-view-engine-switch"
            onKeyDown={handleEngineTablistKey}
            className="flex gap-1"
          >
            <button
              ref={engineSketchTabRef}
              type="button"
              role="tab"
              aria-selected={effectiveEngine === 'sketch'}
              tabIndex={effectiveEngine === 'sketch' ? 0 : -1}
              data-testid="response-view-engine-sketch"
              onClick={() => void handleSelectEngine('sketch')}
              disabled={setLeadStatus === 'saving'}
              className={`rounded-none border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-60 ${
                effectiveEngine === 'sketch'
                  ? 'border-taupe-900 bg-cream-200 text-fg'
                  : 'border-taupe-500 text-taupe-700'
              }`}
            >
              Sketch
            </button>
            <button
              ref={engineImageTabRef}
              type="button"
              role="tab"
              aria-selected={effectiveEngine === 'llm-image'}
              tabIndex={effectiveEngine === 'llm-image' ? 0 : -1}
              data-testid="response-view-engine-llm-image"
              onClick={() => void handleSelectEngine('llm-image')}
              disabled={setLeadStatus === 'saving'}
              className={`rounded-none border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-60 ${
                effectiveEngine === 'llm-image'
                  ? 'border-taupe-900 bg-cream-200 text-fg'
                  : 'border-taupe-500 text-taupe-700'
              }`}
            >
              LLM image
            </button>
          </div>
        ) : null}

        {/* LV3-S2 — engine-switch persist notice. Mounts only when the switch is
            available, so the switch-OFF path stays byte-identical. A failed PUT
            rolls the lead back (above) AND surfaces this one-line notice. */}
        {canSwitchEngine ? (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-testid="response-view-engine-switch-notice"
            data-set-lead-status={setLeadStatus}
            className={`text-xs ${
              setLeadStatus === 'error' ? 'text-accent-red' : 'text-transparent'
            }`}
          >
            {setLeadStatus === 'error' && setLeadError !== null
              ? `Could not switch lead: ${humanizeServiceError(setLeadError)}`
              : null}
          </div>
        ) : null}

        {/* LV3-S4 — escalation rationale + one-click "Back to sketch". Renders
            ONLY when the view was AUTO-escalated to the llm-image lead (LV2-S3)
            AND a raster is genuinely available/loading (LV2-S5 guard). It is a
            CALM one-liner (muted taupe, not an alarm) explaining why an AI image
            appeared, plus a prominent control that resets the sticky leadEngine
            back to the precise sketch via the SAME handler as the engine switch
            (`handleSelectEngine('sketch')` → persist + rollback). The
            illustrative badge + the SysML source tab below are untouched, so
            both stay one action away. Absent ⇒ byte-identical to the
            non-escalated path. */}
        {showEscalationNotice ? (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            data-testid="response-view-escalation-notice"
            data-escalation-reason={props.escalated}
            className="border-taupe-300 bg-cream-50 text-taupe-700 flex flex-col gap-2 rounded-none border p-2 text-xs sm:flex-row sm:items-center sm:justify-between"
          >
            <span>
              This request is beyond the precise renderer — here&rsquo;s an AI
              illustration. The exact sketch + SysML source are one click away.
            </span>
            <button
              type="button"
              data-testid="response-view-back-to-sketch"
              aria-label="Back to the precise sketch"
              onClick={() => void handleBackToSketch()}
              disabled={setLeadStatus === 'saving'}
              className="border-taupe-900 hover:bg-cream-200 text-fg shrink-0 self-start rounded-none border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
            >
              Back to sketch
            </button>
          </div>
        ) : null}

        {/* IV3-S2 — [ Diagram | SysML source ] segmented control. Present on
            EVERY view (pillar invariant). Keyboard-operable per WAI-ARIA tabs. */}
        <div
          role="tablist"
          aria-label="View representation"
          data-testid="response-view-source-tabs"
          onKeyDown={handleTablistKey}
          className="flex gap-1"
        >
          <button
            ref={diagramTabRef}
            type="button"
            role="tab"
            aria-selected={tab === 'diagram'}
            tabIndex={tab === 'diagram' ? 0 : -1}
            data-testid="response-view-tab-diagram"
            onClick={() => setTab('diagram')}
            className={`rounded-none border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${
              tab === 'diagram'
                ? 'border-taupe-900 bg-cream-200 text-fg'
                : 'border-taupe-500 text-taupe-700'
            }`}
          >
            Diagram
          </button>
          <button
            ref={sysmlTabRef}
            type="button"
            role="tab"
            aria-selected={tab === 'sysml'}
            tabIndex={tab === 'sysml' ? 0 : -1}
            data-testid="response-view-tab-sysml"
            onClick={() => void activateSysmlTab()}
            className={`rounded-none border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide ${
              tab === 'sysml'
                ? 'border-taupe-900 bg-cream-200 text-fg'
                : 'border-taupe-500 text-taupe-700'
            }`}
          >
            SysML source
          </button>
        </div>

        {tab === 'sysml' ? (
          <div
            role="tabpanel"
            data-testid="response-view-sysml-panel"
            aria-label="Raw SysML v2 source"
            className="flex flex-col gap-1"
          >
            {sysmlLoading ? (
              <div
                role="status"
                data-testid="response-view-sysml-loading"
                className="bg-cream-200 animate-pulse rounded-none border border-taupe-300"
                style={{ width: 560, height: 120 }}
                aria-label="Loading SysML source…"
              />
            ) : sysmlSource !== null ? (
              <>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    data-testid="response-view-sysml-copy"
                    aria-label="Copy SysML source"
                    onClick={() => {
                      void navigator.clipboard?.writeText(sysmlSource);
                    }}
                    className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  >
                    Copy
                  </button>
                  <button
                    type="button"
                    data-testid="response-view-sysml-open"
                    aria-label="Open SysML source in file viewer"
                    onClick={handleOpen}
                    className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                  >
                    Open in file viewer
                  </button>
                </div>
                <pre
                  data-testid="response-view-sysml-pre"
                  className="border-taupe-300 bg-cream-50 text-fg max-h-[260px] overflow-auto rounded-none border p-2 text-[11px] leading-snug"
                >
                  <code>{sysmlSource}</code>
                </pre>
              </>
            ) : (
              <div
                data-testid="response-view-sysml-unavailable"
                className="text-taupe-700 text-xs"
              >
                SysML source unavailable for this view.
              </div>
            )}
          </div>
        ) : null}

        {/* LV3-S2 — raster lead surface. Shown on the Diagram tab when the
            sticky lead engine is 'llm-image'. Carries a NON-DISMISSIBLE
            illustrative badge (source-of-truth pillar) and a click-to-expand
            lightbox; the authoritative SysML source stays one tab away. */}
        {effectiveEngine === 'llm-image' &&
        props.rasterImageUrl !== undefined ? (
          <div
            className="overflow-hidden"
            data-testid="response-view-raster-lead"
            hidden={tab !== 'diagram'}
          >
            <span
              data-testid="response-view-raster-illustrative-badge"
              className="border-amber-500 bg-amber-50 text-amber-900 mb-1 inline-block self-start rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            >
              {ILLUSTRATIVE_BADGE_COPY}
            </span>
            <button
              type="button"
              data-testid="response-view-raster-expand"
              aria-label="Expand illustrative image"
              onClick={() => setRasterModalOpen(true)}
              className="block cursor-zoom-in border-0 bg-transparent p-0 text-left"
            >
              <img
                data-testid="response-view-raster-img"
                src={props.rasterImageUrl}
                alt={props.rasterAlt ?? `Illustrative diagram for ${props.viewName}`}
                className="block max-h-[260px] max-w-full object-contain"
              />
            </button>
          </div>
        ) : null}

        {/* Phase 2 — non-dismissible "N parts hidden" faithfulness badge. Shows
            whenever the sketch is scoped (some parts hidden/collapsed) so a
            partial view can never be mistaken for the complete model. "Show all"
            clears the scope (the raw view.sysml is untouched throughout — the
            full model is always one action away under "SysML source"). Tracks
            the sketch surface (same predicate as the preview below). */}
        {isScoped && tab === 'diagram' && effectiveEngine === 'sketch' ? (
          <div
            data-testid="response-view-scope-badge"
            role="status"
            className="border-amber-500 bg-amber-50 text-amber-900 mb-1 flex items-center gap-2 self-start rounded-none border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
          >
            <span>
              {hiddenCount} {hiddenCount === 1 ? 'part' : 'parts'} hidden — showing a partial view
            </span>
            {props.onClearScope !== undefined ? (
              <button
                type="button"
                data-testid="response-view-scope-show-all"
                aria-label="Show all parts"
                onClick={() => void handleShowAll()}
                disabled={scopeStatus === 'clearing'}
                className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50"
              >
                {scopeStatus === 'clearing' ? 'Showing…' : 'Show all'}
              </button>
            ) : null}
            {scopeStatus === 'error' && scopeError !== null ? (
              <span data-testid="response-view-scope-error" className="text-accent-red normal-case">
                {humanizeServiceError(scopeError)}
              </span>
            ) : null}
          </div>
        ) : null}

        <div
          className="reactflow-preview overflow-hidden"
          data-testid="response-view-proposal-preview"
          hidden={tab !== 'diagram' || effectiveEngine !== 'sketch'}
        >
          {previewLoading ? (
            <div
              role="status"
              aria-label="Rendering view…"
              data-testid="response-view-proposal-skeleton"
              className="bg-cream-200 animate-pulse rounded-none border border-taupe-300"
              style={{ width: 560, height: 220 }}
            />
          ) : previewError ? (
            <div data-testid="response-view-proposal-error" className="text-sm">
              <div className="text-accent-red mb-1">View render failed.</div>
              <button
                type="button"
                aria-label="Re-render view"
                data-testid="response-view-proposal-rerender"
                onClick={() => void fetchLayout()}
                className="px-2 py-1 rounded-none border border-taupe-500 hover:border-taupe-900 text-fg text-xs uppercase tracking-wide"
              >
                Re-render
              </button>
            </div>
          ) : layout !== null ? (
            <button
              type="button"
              data-testid="response-view-proposal-expand"
              aria-label="Expand view"
              onClick={handleExpand}
              className="cursor-zoom-in border-0 bg-transparent p-0 text-left"
            >
              <div data-testid="response-view-proposal-reactflow">
                <MiniReactFlowPreview layout={layout} width={800} height={500} />
              </div>
            </button>
          ) : null}
        </div>

        {/* LV3-S3 — inline "adjust layout" refinement prompt. Renders ONLY when
            a handler is wired AND the sketch is leading; hidden (but mounted)
            when the visible tab isn't the Diagram tab so its live-region keeps
            announcing. The submit routes through the chat send path (LV1-S6):
            the server re-lays-out the CURRENT view deterministically (~1–2 s,
            re-emitted kb:viewRendered, in place) or — on compiler
            `unsupported` — escalates to the raster sibling. The card itself
            never re-shows an unchanged sketch. NOT gated on `llmImageEnabled`:
            the deterministic tweak loop is always-on. Absent ⇒ byte-identical
            to the raster-disabled / older-caller path. */}
        {canAdjustLayout ? (
          <div
            className="flex flex-col gap-1"
            data-testid="response-view-adjust-layout"
            hidden={tab !== 'diagram'}
          >
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={adjustText}
                onChange={(e) => setAdjustText(e.target.value)}
                onKeyDown={handleAdjustKey}
                disabled={adjustStatus === 'submitting'}
                data-testid="response-view-adjust-layout-input"
                aria-label="Edit the diagram — content, layout or visibility"
                placeholder="Edit the diagram — e.g. “add the elements connected to the BatteryPack”, “move the BMS up”"
                className="border-taupe-500 focus:border-taupe-900 text-fg flex-1 rounded-none border bg-cream-50 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
              />
              <button
                type="button"
                data-testid="response-view-adjust-layout-submit"
                aria-label="Apply layout adjustment"
                onClick={() => void handleAdjustLayout()}
                disabled={adjustStatus === 'submitting' || adjustText.trim().length === 0}
                className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50"
              >
                {adjustStatus === 'submitting' ? 'Adjusting…' : 'Adjust'}
              </button>
            </div>
            {/* Live-region notice. Mounted with the prompt so successive
                submits announce. Success is a transient "Adjusting layout…"
                (the re-render arrives out-of-band); error surfaces the message
                and the input re-enables — no spinner left hanging. */}
            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-testid="response-view-adjust-layout-notice"
              data-adjust-status={adjustStatus}
              className={`text-xs ${
                adjustStatus === 'error'
                  ? 'text-accent-red'
                  : adjustStatus === 'submitted'
                    ? 'text-emerald-700'
                    : 'text-transparent'
              }`}
            >
              {adjustStatus === 'submitted'
                ? 'Adjusting layout…'
                : adjustStatus === 'error' && adjustError !== null
                  ? `Could not adjust layout: ${humanizeServiceError(adjustError)}`
                  : null}
            </div>
          </div>
        ) : null}

        {/* LV3-S1 (enhanced) — CONVERSATIONAL "Generate LLM visualization"
            follow-up OFFER. Rendered ONLY when the server marked the view
            rasterOffered AND the views.llmImage.enabled master switch is ON AND
            a handler is wired (`canOfferRaster`) AND no raster exists yet for
            this view (`rasterReady !== true` — once one does, the engine
            switcher takes over) AND none is currently GENERATING
            (`rasterLoading !== true`). The `rasterLoading` guard is
            load-bearing: an AUTO-escalation (LV2-S3) fires its own raster while
            `rasterReady` is still false, so without it the offer would
            co-render with the LV3-S4 escalation rationale (two contradictory
            AI-render affordances) AND its Generate button could fire a
            redundant second paid render for a view already generating one.
            Switch-OFF ⇒ this entire subtree is absent (byte-identical to the
            deterministic-only path; the sketch leads alone). It reads as Tessa
            offering a next step: an AI render is available, it can honour extra
            layout wishes (the optional input), and it takes ~a minute. The
            exact sketch + SysML source stay one action away (the tabs + preview
            above are untouched). NOT gated on the sketch leading — the offer is
            about producing the raster, not editing the sketch. */}
        {canOfferRaster &&
        props.rasterReady !== true &&
        props.rasterLoading !== true ? (
          <div
            data-testid="response-view-raster-offer"
            className="border-taupe-300 bg-cream-50 flex flex-col gap-2 rounded-none border p-2"
          >
            {/* Collapsed disclosure header — the user must ACTIVATE (open) this
                drop-down before the paid, ~minute-long AI-visualization action
                is available. Default collapsed keeps the sketch the uncluttered
                default and prevents accidental triggering of the image path. */}
            <button
              type="button"
              data-testid="response-view-raster-offer-toggle"
              aria-expanded={offerExpanded}
              onClick={() => setOfferExpanded((v) => !v)}
              className="text-fg flex items-center gap-1.5 self-start text-left text-xs font-semibold"
            >
              <span
                aria-hidden="true"
                className={`inline-block transition-transform ${
                  offerExpanded ? 'rotate-90' : ''
                }`}
              >
                ▸
              </span>
              Generate an AI visualization (optional)
            </button>

            {offerExpanded ? (
              <div className="flex flex-col gap-2">
                <div
                  className="text-fg text-xs"
                  data-testid="response-view-raster-offer-copy"
                >
                  Want an AI-rendered visualization of this diagram? It can take
                  extra layout wishes into account (e.g.{' '}
                  &ldquo;group by subsystem&rdquo;, &ldquo;make it look like a
                  real car&rdquo;), but it takes about a minute and is an
                  illustration only. The exact sketch and SysML source stay one
                  click away.
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={layoutWishesText}
                    onChange={(e) => setLayoutWishesText(e.target.value)}
                    onKeyDown={handleWishesKey}
                    disabled={generateStatus === 'generating'}
                    data-testid="response-view-raster-offer-wishes"
                    aria-label="Optional layout wishes for the AI visualization"
                    placeholder="Optional: describe layout wishes…"
                    className="border-taupe-500 focus:border-taupe-900 text-fg flex-1 rounded-none border bg-cream-50 px-2 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <button
                    type="button"
                    data-testid="response-view-generate-llm"
                    aria-label="Generate LLM visualization"
                    onClick={() => void handleGenerateImage()}
                    disabled={generateStatus === 'generating'}
                    className="border-taupe-500 hover:border-taupe-900 text-fg shrink-0 rounded-none border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {generateStatus === 'generating'
                      ? 'Generating…'
                      : generateStatus === 'requested'
                        ? 'Visualization requested'
                        : 'Generate LLM visualization'}
                  </button>
                </div>
                {/* Generation notice live-region. On a non-OK 202 accept this
                    surfaces a CALM one-line notice (opaque transport errors are
                    humanized — no raw "500 Internal Server Error") and the button
                    re-enables (no infinite spinner); the sketch above is never
                    replaced. On success the raster resolves in its sibling card. */}
                <div
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  data-testid="response-view-generate-llm-notice"
                  data-generate-status={generateStatus}
                  className={`text-xs ${
                    generateStatus === 'error'
                      ? 'text-accent-red'
                      : generateStatus === 'requested'
                        ? 'text-emerald-700'
                        : 'text-transparent'
                  }`}
                >
                  {generateStatus === 'requested'
                    ? 'Generating an AI visualization — it will appear below. This can take about a minute.'
                    : null}
                  {generateStatus === 'error' && generateError !== null
                    ? `Visualization failed: ${humanizeServiceError(generateError)}`
                    : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          data-testid="response-view-proposal-toast"
          data-toast-status={saveStatus}
          className={`flex-1 text-xs ${
            saveStatus === 'saved'
              ? 'text-emerald-700'
              : saveStatus === 'error'
                ? 'text-accent-red'
                : 'text-transparent'
          }`}
        >
          {saveStatus === 'saved' ? 'Saved ✓' : null}
          {saveStatus === 'error' && saveError !== null
            ? `Save failed: ${humanizeServiceError(saveError)}`
            : null}
          {/* idle / saving render nothing visible but the live region stays
              mounted so the next status update announces correctly. */}
        </div>
        {props.onSave !== undefined ? (
          <button
            type="button"
            data-testid="response-view-proposal-save"
            aria-label="Save view"
            onClick={() => void handleSave()}
            disabled={layout === null || saveStatus === 'saving'}
            className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveStatus === 'saving' ? 'Saving…' : 'Save'}
          </button>
        ) : null}
        {/* LV3-S1 — the "Generate LLM visualization" action now lives in the
            conversational offer block above (with its optional layout-wishes
            input + latency copy), not as a bare footer button. The footer keeps
            only the Save + Edit-in-Views affordances. */}
        <button
          type="button"
          data-testid="response-view-proposal-open"
          aria-label="Edit view in Views tab"
          onClick={handleOpen}
          className="border-taupe-500 hover:border-taupe-900 text-fg rounded-none border px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
        >
          Edit in Views
        </button>
      </div>

      <ViewImageModal
        open={modalOpen}
        layout={layout}
        viewId={props.viewId}
        viewName={layout?.title ?? props.viewName}
        onClose={handleCloseModal}
        {...(props.onSave !== undefined
          ? {
              onSave: (l: ReactFlowLayoutJson) => props.onSave!(props.viewId, l),
            }
          : {})}
        {...(props.onEditViewContent ?? props.onAdjustLayout) !== undefined
          ? {
              // Diagram EDIT MODE — clicking the preview opened this focused
              // view. Its composer routes through the DIRECT view-scoped diagram
              // agent (`onEditViewContent` → POST /views/:viewId/edit) when
              // wired, so the edit is interpreted + executed against THIS view
              // and re-renders in place via the kb:viewRendered → layoutRevision
              // re-fetch (a fresh `layout` prop) — WITHOUT landing a turn in the
              // normal chat. Falls back to the chat-backed `onAdjustLayout` for
              // older callers / tests that don't wire the direct path.
              onEditRequest: (request: string) =>
                (props.onEditViewContent ?? props.onAdjustLayout)!(
                  props.viewId,
                  request,
                ),
            }
          : {}}
        /* Diagram-mode LLM raster (RB ruling 2026-07-07): the focused view hosts
           a "Generate AI image" button + a Diagram <-> AI image toggle in place,
           mirroring the Views tab. Gated on the LIVE master switch
           (llmImageEnabledLive) — NOT the envelope's rasterOffered/llmImageEnabled,
           which the server marks per-view — so the button appears whenever raster
           is enabled for the workspace, exactly like the Views tab. Passing
           rasterImageSrc here does NOT surface the raster in the inline card body
           (that path needs onSetLeadEngine, never wired by the stacked-cards
           slot), so the raster shows ONLY in this focused modal. */
        {...(llmImageEnabledLive ? { llmImageEnabled: true } : {})}
        {...(props.onGenerateImage !== undefined
          ? {
              onGenerateImage: (wishes?: string) =>
                wishes !== undefined && wishes.length > 0
                  ? props.onGenerateImage!(props.viewId, wishes)
                  : props.onGenerateImage!(props.viewId),
            }
          : {})}
        {...(props.rasterImageUrl !== undefined
          ? { rasterImageSrc: props.rasterImageUrl }
          : {})}
        {...(props.rasterAlt !== undefined
          ? { rasterImageAlt: props.rasterAlt }
          : {})}
        {...(props.rasterLoading === true ? { rasterGenerating: true } : {})}
        {...(props.rasterError === true ? { rasterError: true } : {})}
      />

      {/* LV3-S2 — raster lightbox (image-mode modal: layout=null + imageSrc
          activates the fullscreen `<img>` + illustrative-badge branch). */}
      {props.rasterImageUrl !== undefined ? (
        <ViewImageModal
          open={rasterModalOpen}
          layout={null}
          viewId={props.viewId}
          viewName={layout?.title ?? props.viewName}
          onClose={() => setRasterModalOpen(false)}
          imageSrc={props.rasterImageUrl}
          imageAlt={props.rasterAlt ?? `Illustrative diagram for ${props.viewName}`}
        />
      ) : null}
    </CardShell>
  );
}
